/**
 * useSimilarListings Hook
 * Fetches similar listings for a given listing ID
 * Extracted from SimilarListings component to comply with component-no-api-calls rule
 */

import { useState, useEffect, useRef } from 'react';
import { Product } from '../contexts/ProductProvider';
import { trackError, ErrorSeverity } from '../utils/error-tracking';
import { encodeAffinityParam } from '../utils/affinity-param';
import { sessionAffinityTags } from '../utils/session-interest';
import { getAnonymousId } from '../utils/tracking-context';
import { isEdgeRefusal, isForeignRefusal, isNetworkFailure, parseJsonResponse } from '../utils/read-json-response';
import { appendPageLoadAt, appendShuffleSeed } from '../utils/seeded-shuffle';
import { asError } from './as-error';

/** The /api/search/similar response, success or failure. */
interface SimilarListingsPage {
  success?: boolean;
  error?: string;
  listings?: Product[];
}

/** Pause before re-asking after a rejected fetch while the browser says it is online. */
export const NETWORK_RETRY_DELAY_MS = 1500;

interface UseSimilarListingsOptions {
  listingId: number;
  isVisible: boolean;
  similarList: Product[];
  currentListGroup: string | null;
  setSimilarListings: (listings: Product[]) => void;
}

interface UseSimilarListingsReturn {
  listings: Product[];
  isLoading: boolean;
  hasError: boolean;
}

export function useSimilarListings({
  listingId,
  isVisible,
  similarList,
  currentListGroup,
  setSimilarListings,
}: UseSimilarListingsOptions): UseSimilarListingsReturn {
  const [listings, setListings] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const lastFetchedListingId = useRef<number | null>(null);

  // Fetch similar listings when visible or when listing changes
  useEffect(() => {
    if (!isVisible || !listingId) return;

    // When navigating through similarList, keep using the existing list
    if (currentListGroup === 'similarList' && similarList.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- adopts the already-fetched similarList from product context when navigating within it, instead of firing a duplicate request
      setListings(similarList);
      setIsLoading(false);
      return;
    }

    // If similarList was preloaded (via hover on order-info), use it directly
    if (similarList.length > 0 && lastFetchedListingId.current !== listingId) {
      setListings(similarList);
      lastFetchedListingId.current = listingId;
      setIsLoading(false);
      return;
    }

    // Skip if we already fetched for this listing
    if (lastFetchedListingId.current === listingId) {
      return;
    }

    // A rejected fetch gets one more try before it is reported. The rows that
    // reached triage were a device asleep or offline when the section came into
    // view (one Chromebook fired it 19 minutes after its last page view, with
    // navigator.onLine false), so: offline waits for the `online` event, online
    // retries after a short pause. Only a second failure is logged.
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    // The cleanup clears the timer and removes the listener, so a retry never
    // runs for a listing the page has left.
    const retry = () => {
      void fetchSimilarListings(true);
    };

    const fetchSimilarListings = async (isRetry = false) => {
      let retrying = false;
      setIsLoading(true);
      setHasError(false);

      try {
        // The visit's own interest vector and id ride along so the server can
        // personalise these rows when the switch is on, and pick the ranking
        // arm. Both are ignored while `searchPersonalization` is off.
        const params = new URLSearchParams({ listingId: String(listingId), limit: '12' });
        const affinity = encodeAffinityParam(sessionAffinityTags());
        if (affinity) params.append('affinity', affinity);
        const anonymousId = getAnonymousId();
        if (anonymousId) params.append('anonymousId', anonymousId);

        const response = await fetch(`/api/search/similar?${appendPageLoadAt(appendShuffleSeed(params))}`);
        const data = await parseJsonResponse<SimilarListingsPage>(response, 'GET /api/search/similar');

        if (data.success) {
          const fetchedListings = data.listings || [];
          setListings(fetchedListings);
          lastFetchedListingId.current = listingId;
          // Store in ProductProvider so flip buttons can use them
          setSimilarListings(fetchedListings);
        } else if (response.status === 404) {
          // A 404 used to mean "not indexed yet", which was almost all of them:
          // measured over five days on production, 94 occurrences in 98 were a
          // listing under an hour old. The endpoint now falls back to the
          // database for exactly that case, so what is left here is narrower and
          // more meaningful — a listing that does not exist, or one whose status
          // is not public (ARCHIVED, which is also what a soft delete writes).
          // A viewer landing on a delisted listing's product page is ordinary,
          // so this is info, not an error -- it was a warning until 2026-09-23,
          // which kept 179 of them in triage -- and still leaves hasError alone:
          // the section hides exactly as an empty result would.
          // Remember the id as a completed fetch — the answer will not change
          // while the viewer is on the page, and re-asking on every render is
          // how a warning becomes a storm.
          trackError(`[SimilarListings] API error: ${data.error}`, ErrorSeverity.INFO, {
            component: 'SimilarListings',
            action: 'fetch-similar-listings',
            listing_id: listingId,
            tags: { http_status: '404' },
          });
          lastFetchedListingId.current = listingId;
        } else {
          trackError(`[SimilarListings] API error: ${data.error}`, ErrorSeverity.ERROR, {
            component: 'SimilarListings',
            action: 'fetch-similar-listings',
            listing_id: listingId,
            tags: { http_status: String(response.status) },
          });
          setHasError(true);
        }
      } catch (err) {
        // A non-JSON 401/403 was written by the edge before our API ran: a
        // refused visitor, not a fault in this code, so it goes in at warning.
        // A rejected fetch (TypeError "Failed to fetch") never reached our API:
        // a dropped connection or a cancelled request, also a warning. The
        // online flag and listing id let the next reader tell offline from
        // an extension block.
        const networkFailure = isNetworkFailure(err);
        if (networkFailure && !isRetry) {
          if (cancelled) return;
          if (navigator.onLine === false) {
            window.addEventListener('online', retry, { once: true });
          } else {
            // A short retry keeps the spinner up; an offline wait (above) may
            // run for minutes, so it shows nothing instead.
            retrying = true;
            retryTimer = setTimeout(retry, NETWORK_RETRY_DELAY_MS);
          }
          return;
        }
        // A refusal with no Vercel header was written by the visitor's own
        // network (one proxy produced every such row on 2026-10-01): info.
        const severity = isForeignRefusal(err)
          ? ErrorSeverity.INFO
          : isEdgeRefusal(err) || networkFailure
            ? ErrorSeverity.WARNING
            : ErrorSeverity.ERROR;
        trackError(asError(err), severity, {
          component: 'SimilarListings',
          action: 'fetch-similar-listings',
          ...(networkFailure && {
            listing_id: listingId,
            tags: {
              network_failure: 'true',
              online: String(navigator.onLine),
              retried: 'true',
            },
          }),
        });
        setHasError(true);
      } finally {
        if (!retrying) setIsLoading(false);
      }
    };

    void fetchSimilarListings();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      window.removeEventListener('online', retry);
    };
  }, [isVisible, listingId, setSimilarListings, similarList, currentListGroup]);

  return { listings, isLoading, hasError };
}
